/* The warehouse photo booth. Every filter is a small canvas routine: a tint,
   a stamp, a prop. Nothing leaves the device unless the person shares it. */
const FONT = '"Barlow Condensed", "Barlow", sans-serif';

export const FILTERS = [
  { key: 'received', name: 'Received', hint: 'Booked in Odoo. Destination: OUT.' },
  { key: 'fragile', name: 'Fragile', hint: 'Handle with care. This side up.' },
  { key: 'crossdock', name: 'Cross-dock', hint: 'On hold until the rest of the team arrives.' },
  { key: 'backorder', name: 'Backorder', hint: 'The rest of you ships next week.' },
  { key: 'oversize', name: 'Oversize', hint: 'Ships on pallet. Mind the doorframe.' },
  { key: 'inny', name: 'Inny', hint: 'Visor on, scanning for cartons.' },
  { key: 'wrapped', name: 'Pallet-wrapped', hint: 'Shrink-wrapped for transport.' },
  { key: 'hivis', name: 'Hi-vis', hint: 'Forklift certified. Probably.' },
];

const rand = (seed) => {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
};

export async function renderSelfie(bitmap, key, { maxSide = 1080 } = {}) {
  const bw = bitmap.naturalWidth || bitmap.width;
  const bh = bitmap.naturalHeight || bitmap.height;
  const scale = Math.min(1, maxSide / Math.max(bw, bh));
  const w = Math.round(bw * scale);
  const h = Math.round(bh * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  await document.fonts?.load(`bold 40px ${FONT}`).catch(() => {});
  const filter = RENDER[key] || RENDER.received;
  filter(ctx, bitmap, w, h);
  return canvas;
}

const base = (ctx, bmp, w, h, cssFilter = 'none') => {
  ctx.save();
  ctx.filter = cssFilter;
  ctx.drawImage(bmp, 0, 0, w, h);
  ctx.restore();
};

const u = (w, h) => Math.min(w, h) / 100; // one "unit": sizes scale with the photo

function stamp(ctx, w, h, text, color, { y = 0.5, angle = -0.2, size = 16, sub = '' } = {}) {
  const k = u(w, h);
  ctx.save();
  ctx.translate(w / 2, h * y);
  ctx.rotate(angle);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // the frame fits the wider of the two lines and never leaves the photo
  const widthAt = (px) => {
    ctx.font = `bold ${px}px ${FONT}`;
    let wd = ctx.measureText(text).width;
    if (sub) {
      ctx.font = `600 ${px * 0.42}px ${FONT}`;
      wd = Math.max(wd, ctx.measureText(sub).width);
    }
    return wd + 6 * k;
  };
  let px = size * k;
  let tw = widthAt(px);
  if (tw > w * 0.9) {
    px *= (w * 0.9) / tw;
    tw = widthAt(px);
  }
  size = px / k;
  ctx.font = `bold ${size * k}px ${FONT}`;
  const th = size * k * 1.15 + (sub ? size * k * 0.55 : 0);
  ctx.lineWidth = 1.2 * k;
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.9;
  ctx.strokeRect(-tw / 2, -th / 2, tw, th);
  ctx.fillStyle = color;
  ctx.fillText(text, 0, sub ? -size * k * 0.25 : 0);
  if (sub) {
    ctx.font = `600 ${size * k * 0.42}px ${FONT}`;
    ctx.fillText(sub, 0, size * k * 0.45);
  }
  ctx.restore();
}

function tag(ctx, w, h, text, bg, fg, { x = 0.05, y = 0.86, size = 6 } = {}) {
  const k = u(w, h);
  ctx.save();
  ctx.font = `bold ${size * k}px ${FONT}`;
  ctx.textBaseline = 'middle';
  const tw = ctx.measureText(text).width + 4 * k;
  const th = size * k * 1.6;
  const px = w * x;
  const py = h * y;
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.roundRect(px, py, tw, th, 1.5 * k);
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.fillText(text, px + 2 * k, py + th / 2);
  ctx.restore();
}

function barcode(ctx, w, h, { y = 0.9, seed = 7 } = {}) {
  const k = u(w, h);
  const r = rand(seed);
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.globalAlpha = 0.9;
  ctx.fillRect(w * 0.55, h * y - 1 * k, w * 0.4, 9 * k);
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#111827';
  let x = w * 0.56;
  while (x < w * 0.94) {
    const bw = (0.4 + r() * 1.4) * k;
    if (r() > 0.4) ctx.fillRect(x, h * y, bw, 6 * k);
    x += bw + 0.3 * k;
  }
  ctx.font = `600 ${2.4 * k}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.fillText(`5400${String(Math.floor(r() * 1e9)).padStart(9, '0')}`, w * 0.75, h * y + 7.6 * k);
  ctx.restore();
}

function stripes(ctx, w, h, { alpha = 0.22, gap = 9, angle = Math.PI / 5 } = {}) {
  const k = u(w, h);
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.rotate(angle);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = '#ffffff';
  const span = Math.hypot(w, h);
  for (let x = -span; x < span; x += gap * k) ctx.fillRect(x, -span, 2.5 * k, 2 * span);
  ctx.restore();
}

function bulge(ctx, w, h, strength = 0.55, radius = 0.42) {
  const src = ctx.getImageData(0, 0, w, h);
  const dst = ctx.createImageData(w, h);
  const cx = w / 2;
  const cy = h * 0.42;
  const R = Math.min(w, h) * radius;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const dx = x - cx;
      const dy = y - cy;
      const d = Math.hypot(dx, dy);
      let sx = x;
      let sy = y;
      if (d < R) {
        const t = d / R;
        const f = 1 - strength * (1 - t) * (1 - t);
        sx = Math.round(cx + dx * f);
        sy = Math.round(cy + dy * f);
      }
      const si = (Math.min(h - 1, Math.max(0, sy)) * w + Math.min(w - 1, Math.max(0, sx))) * 4;
      const di = (y * w + x) * 4;
      dst.data[di] = src.data[si];
      dst.data[di + 1] = src.data[si + 1];
      dst.data[di + 2] = src.data[si + 2];
      dst.data[di + 3] = 255;
    }
  }
  ctx.putImageData(dst, 0, 0);
}

function visor(ctx, w, h) {
  const k = u(w, h);
  const vy = h * 0.3;
  const vh = 16 * k;
  const vw = w * 0.74;
  ctx.save();
  ctx.fillStyle = '#161F2B';
  ctx.beginPath();
  ctx.roundRect((w - vw) / 2, vy, vw, vh, 6 * k);
  ctx.fill();
  ctx.fillStyle = '#0B1017';
  ctx.beginPath();
  ctx.roundRect((w - vw) / 2 + 3 * k, vy + 3 * k, vw - 6 * k, vh - 6 * k, 4 * k);
  ctx.fill();
  ctx.fillStyle = '#3EE39A';
  for (const fx of [0.36, 0.64]) {
    ctx.beginPath();
    ctx.roundRect(w * fx - 7 * k, vy + vh / 2 - 1.5 * k, 14 * k, 3 * k, 1.5 * k);
    ctx.fill();
  }
  // antenna
  ctx.fillStyle = '#3B4A5C';
  ctx.fillRect(w / 2 - 1 * k, vy - 10 * k, 2 * k, 10 * k);
  ctx.fillStyle = '#20885C';
  ctx.beginPath();
  ctx.arc(w / 2, vy - 10 * k, 2.4 * k, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function hardhat(ctx, w, h) {
  const k = u(w, h);
  ctx.save();
  ctx.fillStyle = '#F5B301';
  ctx.beginPath();
  ctx.ellipse(w / 2, h * 0.2, 24 * k, 15 * k, 0, Math.PI, 0);
  ctx.fill();
  ctx.fillRect(w / 2 - 30 * k, h * 0.2 - 1 * k, 60 * k, 4 * k);
  ctx.fillStyle = '#d99a00';
  ctx.fillRect(w / 2 - 3 * k, h * 0.2 - 15 * k, 6 * k, 15 * k);
  ctx.restore();
}

function vest(ctx, w, h) {
  const k = u(w, h);
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = '#F5B301';
  ctx.beginPath();
  ctx.moveTo(w * 0.1, h);
  ctx.lineTo(w * 0.22, h * 0.68);
  ctx.lineTo(w * 0.38, h * 0.74);
  ctx.lineTo(w * 0.5, h * 0.9);
  ctx.lineTo(w * 0.62, h * 0.74);
  ctx.lineTo(w * 0.78, h * 0.68);
  ctx.lineTo(w * 0.9, h);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#d0d5dc';
  for (const yy of [0.84, 0.92]) ctx.fillRect(w * 0.14, h * yy, w * 0.72, 2.5 * k);
  ctx.restore();
}

const RENDER = {
  received(ctx, bmp, w, h) {
    base(ctx, bmp, w, h, 'contrast(1.05) saturate(1.1)');
    stamp(ctx, w, h, 'RECEIVED', '#15803d', { y: 0.58, size: 18, sub: `${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} · OUT · ship now` });
    tag(ctx, w, h, 'Validated in Odoo', '#15803d', '#ffffff', { y: 0.05 });
    barcode(ctx, w, h, { y: 0.88, seed: 3 });
  },
  fragile(ctx, bmp, w, h) {
    base(ctx, bmp, w, h, 'saturate(0.85)');
    stamp(ctx, w, h, 'FRAGILE', '#b91c1c', { y: 0.55, angle: 0.18, size: 20, sub: 'HANDLE WITH CARE · THIS SIDE UP ↑' });
    tag(ctx, w, h, '⚠ 1 fragile', '#fde2e1', '#b91c1c', { y: 0.05 });
    tag(ctx, w, h, 'Do not stack', '#111827', '#ffffff', { x: 0.62, y: 0.05, size: 5 });
  },
  crossdock(ctx, bmp, w, h) {
    base(ctx, bmp, w, h, 'sepia(0.5) saturate(1.6) hue-rotate(-15deg) contrast(1.05)');
    stamp(ctx, w, h, 'CROSSDOCK', '#b45309', { y: 0.56, angle: -0.12, size: 17, sub: 'on hold · waiting on the rest of the team' });
    tag(ctx, w, h, 'Set aside for now', '#b45309', '#ffffff', { y: 0.05 });
  },
  backorder(ctx, bmp, w, h) {
    base(ctx, bmp, w, h, 'sepia(0.75) contrast(0.95) brightness(1.05)');
    stripes(ctx, w, h, { alpha: 0.08, gap: 4, angle: 0 });
    stamp(ctx, w, h, 'BACKORDER', '#7c2d12', { y: 0.55, angle: -0.08, size: 17, sub: 'the rest of you ships next week' });
    tag(ctx, w, h, '1 of 2 received', '#fde68a', '#7c2d12', { y: 0.05 });
  },
  oversize(ctx, bmp, w, h) {
    base(ctx, bmp, w, h);
    bulge(ctx, w, h, 0.5, 0.45);
    tag(ctx, w, h, 'Oversize · 1.3 m³ · 42 kg', '#dbeafe', '#1e3a8a', { y: 0.05 });
    tag(ctx, w, h, 'Ships on pallet · mind the doorframe', '#111827', '#ffffff', { y: 0.9, size: 5 });
  },
  inny(ctx, bmp, w, h) {
    base(ctx, bmp, w, h, 'contrast(1.1) saturate(0.9)');
    visor(ctx, w, h);
    tag(ctx, w, h, 'inny · scanning…', '#161F2B', '#3EE39A', { y: 0.9, size: 6 });
    barcode(ctx, w, h, { y: 0.78, seed: 11 });
  },
  wrapped(ctx, bmp, w, h) {
    base(ctx, bmp, w, h, 'brightness(1.05) contrast(0.95)');
    stripes(ctx, w, h, { alpha: 0.26, gap: 7, angle: Math.PI / 5 });
    stripes(ctx, w, h, { alpha: 0.14, gap: 11, angle: -Math.PI / 6 });
    tag(ctx, w, h, 'Shrink-wrapped · 1 pallet announced', '#111827', '#ffffff', { y: 0.05 });
    tag(ctx, w, h, 'Do not open until Monday', '#fde68a', '#7c2d12', { y: 0.9, size: 5 });
  },
  hivis(ctx, bmp, w, h) {
    base(ctx, bmp, w, h, 'contrast(1.08)');
    vest(ctx, w, h);
    hardhat(ctx, w, h);
    tag(ctx, w, h, 'Forklift certified · probably', '#F5B301', '#111827', { y: 0.05 });
  },
};
